import { isUndefined } from 'lodash-es';
import { Children, type ReactElement, type ReactNode } from 'react';

import { applyThresholdDefaults } from './apply-threshold-defaults.js';
import {
  Intent,
  type IntentProps,
} from '../../../../core/slots/Intent/Intent.js';
import { isSlot } from '../../../../core/utils/is-slot.js';
import { ColorRule } from '../../../core/slots/ColorRule.js';
import { Toolbar } from '../../shared/slots/Toolbar.js';
import type { MeterBarChartLegendProps } from '../../shared/types/meter-bar-legend.js';
import {
  handleColorRuleSlot,
  handleIntentSlot,
  handleLabelSlot,
  handleMaxSlot,
  handleMinSlot,
  handleToolbarSlot,
  handleValueSlot,
} from '../../shared/utils/slot-handlers.js';
import { Label } from '../slots/Label.js';
import { Legend } from '../slots/Legend.js';
import { Max } from '../slots/Max.js';
import { Min } from '../slots/Min.js';
import { ThresholdIndicator } from '../slots/ThresholdIndicator.js';
import { ThresholdLegend } from '../slots/ThresholdLegend.js';
import { Tooltip } from '../slots/Tooltip.js';
import { Value } from '../slots/Value.js';
import { MeterBarChartSlots } from '../types/meter-bar-chart-config.js';
import type {
  MeterBarChartThresholdIndicatorProps,
  MeterBarChartTooltipProps,
} from '../types/meter-bar-chart.js';

function handleTooltipSlot(
  child: ReactElement<MeterBarChartTooltipProps>,
  slots: MeterBarChartSlots,
): void {
  const { name, hidden, children } = child.props;
  slots.tooltip = { hidden: hidden ?? false, name, template: children };
}

function handleThresholdSlot(
  child: ReactElement<MeterBarChartThresholdIndicatorProps>,
  slots: MeterBarChartSlots,
) {
  const threshold = applyThresholdDefaults(child.props);
  if (slots.thresholds?.data) {
    slots.thresholds.data.push(threshold);
  } else {
    slots.thresholds = { data: [threshold] };
  }
}

function handleThresholdLegendSlot(slots: MeterBarChartSlots) {
  const threshold = slots.thresholds
    ? { ...slots.thresholds, legend: true }
    : { data: [], legend: true };

  slots.thresholds = { ...threshold };
}

function handleLegendSlot(
  child: ReactElement<MeterBarChartLegendProps>,
  slots: MeterBarChartSlots,
) {
  const legend = !(child.props.hidden ?? true);
  const threshold = slots.thresholds
    ? { ...slots.thresholds, legend }
    : { data: [], legend };

  slots.thresholds = { ...threshold };
}

export function iterateSlots(children?: ReactNode) {
  const slots: MeterBarChartSlots = {
    colorRules: [],
    toolbar: { hidden: false },
  };

  let intents: IntentProps[] | undefined;
  Children.forEach(children, (child) => {
    if (isSlot(child, Label)) {
      handleLabelSlot(child, slots);
    } else if (isSlot(child, Value)) {
      handleValueSlot(child, slots);
    } else if (isSlot(child, Min)) {
      handleMinSlot(child, slots);
    } else if (isSlot(child, Max)) {
      handleMaxSlot(child, slots);
    } else if (isSlot(child, ThresholdIndicator)) {
      handleThresholdSlot(child, slots);
      // eslint-disable-next-line @typescript-eslint/no-deprecated
    } else if (isSlot(child, ThresholdLegend)) {
      handleThresholdLegendSlot(slots);
    } else if (isSlot(child, Legend)) {
      handleLegendSlot(child, slots);
    } else if (isSlot(child, ColorRule)) {
      handleColorRuleSlot(child, slots);
    } else if (isSlot(child, Tooltip)) {
      handleTooltipSlot(child, slots);
    } else if (isSlot(child, Intent)) {
      intents = handleIntentSlot(child, intents);
    } else if (isSlot(child, Toolbar)) {
      handleToolbarSlot(child, slots);
    }
  });

  if (!isUndefined(intents) && slots.toolbar) {
    slots.toolbar.intents = intents;
  }

  return slots;
}
